<template>
  <section class="services" id="services">
    <div class="container">
      <section-title
        data-aos="fade-up"
        :kicker="t('Looking for something else?')"
        :title="t('One-stop professional services')"
        :lede="t('A business needs a website buyers trust and a brand that reads as one company. Bought piecemeal, that is three different vendors to chase down.')"
      />

      <div ref="bento" class="services-bento">
        <Link
          v-for="(item, index) in services"
          :key="item.name"
          class="services-card"
          data-aos="fade-up"
          :class="'is-' + item.name"
          :href="route(item.name)"
        >
          <span class="services-numeral" aria-hidden="true">{{ String(index + 1).padStart(2, '0') }}</span>

          <span v-if="item.badge" class="services-badge">{{ item.badge }}</span>
          <span class="services-icon" aria-hidden="true">
            <font-awesome-icon :icon="item.icon" />
          </span>

          <h3 class="services-card-title">{{ item.title }}</h3>
          <p class="services-body">{{ item.body }}</p>

          <!-- On the dark band, so the chips take the dark ground's treatment. -->
          <tag-list class="services-tags" tone="dark" :tags="item.tags" />

          <app-button
            class="services-more"
            as="span"
            variant="link"
            tone="dark"
            icon="arrow-right"
            icon-after
          >
            {{ t('Learn more') }}
          </app-button>
        </Link>
      </div>

      <marquee class="services-ticker" :label="t('Capabilities we cover')">
        <ul>
          <li v-for="skill in skills" :key="skill">{{ skill }}</li>
        </ul>
      </marquee>
    </div>
  </section>
</template>

<style lang="scss" scoped>
@import '~/_variables.scss';

/* Blueprint ground: two soft blue glows plus a hairlined lattice that fades
   out before the ticker. Tinted to the same $ground-tint the plan table uses, so
   the boundary against the white portfolio section above reads as a change of
   ground rather than one unbroken white run. */
.services {
  background: $ground-tint;
  overflow: hidden;
  position: relative;

  &::before {
    background-image:
      radial-gradient(58rem 30rem at 10% -6%, $tint-primary-soft, transparent 68%),
      radial-gradient(48rem 26rem at 92% 104%, rgba($accent-blue, .09), transparent 68%),
      linear-gradient(rgba($text-heading, .05) 1px, transparent 1px),
      linear-gradient(90deg, rgba($text-heading, .05) 1px, transparent 1px);
    background-size: auto, auto, 4.5rem 4.5rem, 4.5rem 4.5rem;
    content: '';
    inset: 0;
    mask-image: linear-gradient(#000, rgb(0 0 0 / 0%) 82%);
    pointer-events: none;
    position: absolute;
    -webkit-mask-image: linear-gradient(#000, rgb(0 0 0 / 0%) 82%);
  }

  > .container {
    position: relative;
    z-index: 1;
  }
}

/* Bento: 12 columns. The first row is the 7/5 pair it always was; the second
   splits three ways, so five cards read as two uneven rows rather than a plain
   grid. The stagger is what keeps same-size pitches from reading as a card
   grid. */
.services-bento {
  display: grid;
  gap: 1.25rem;
  grid-template-columns: minmax(0, 1fr);

  @media (min-width: 768px) and (max-width: 991.98px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (min-width: 992px) {
    grid-template-columns: repeat(12, minmax(0, 1fr));
  }
}

.services-card {
  /* Horizontal inset lives in a custom property because the badge hangs off the
     top-left edge and has to line up with the card's own text column. */
  --card-pad-x: 1.8rem;
  --card-radius: 1.5rem;

  /* The site's signature blue surface, the same pair the hero, the strength
     band and the CTA use, so the cards read as Veximus rather than a dark
     panel dropped on a light page. The top-left sheen keeps it off a flat
     fill, the way the other blue surfaces carry a texture. */
  background:
    radial-gradient(120% 90% at 0% 0%, $on-dark-fill-strong, transparent 55%),
    $grad-deep;
  border: 1px solid $on-dark-line-strong;
  border-radius: var(--card-radius);
  box-shadow: $shadow-band;
  display: flex;
  flex-direction: column;
  /* Visible so the badge can hang over the top edge. The two decorative
     pseudo-elements clip themselves instead, see below. */
  overflow: visible;
  padding: 1.9rem var(--card-pad-x);
  position: relative;
  transition: border-color $dur $ease, box-shadow $dur $ease, transform $dur $ease;

  @media (min-width: 1400px) {
    --card-pad-x: 2rem;
    padding: 2.15rem var(--card-pad-x);
  }

  /* White glow that tracks the pointer. Position comes from the --mx / --my
     custom properties the section writes on pointermove, so the light never
     needs a repaint of the card itself. */
  &::before {
    background: radial-gradient(24rem 24rem at var(--mx, 50%) var(--my, 0%), $on-dark-line-strong, transparent 62%);
    /* Carries the card's radius itself now that the card no longer clips. */
    border-radius: inherit;
    content: '';
    inset: 0;
    opacity: 0;
    pointer-events: none;
    position: absolute;
    transition: opacity $dur $ease;
  }

  /* Hairline beam along the top edge. Inset by the corner radius so it stops
     short of the rounded corners the card no longer clips. */
  &::after {
    background: linear-gradient(90deg, transparent, $on-dark-body, transparent);
    content: '';
    height: 2px;
    left: var(--card-radius);
    position: absolute;
    right: var(--card-radius);
    top: 0;
    transform: scaleX(0);
    transform-origin: left center;
    transition: transform $dur $ease;
  }

  &:hover,
  &:focus-visible {
    border-color: $on-dark-line-strong;
    box-shadow: $shadow-band, inset 0 0 0 1px $on-dark-line-strong;
    transform: translateY(-4px);

    &::before {
      opacity: 1;
    }

    &::after {
      transform: scaleX(1);
    }

    .services-numeral {
      color: $on-dark-line-strong;
    }
  }

  &:focus-visible {
    outline: 3px solid $on-dark-body;
    outline-offset: 3px;
  }

  /* Five cards, two rows, and the stagger is what keeps same-size pitches from
     reading as a plain card grid. The first row is the 7/5 pair it always was;
     the second row splits three ways, because two more cards would have made a
     7/5 pair plus a three-card row and left the AI card as the odd one out.
     4/4/4 holds the bento's unevenness at the row level while giving the three
     newer services equal room. */
  @media (min-width: 992px) {
    &.is-web-design {
      grid-column: span 7;
    }

    &.is-digital-marketing {
      grid-column: span 5;
    }

    &.is-mobile-application {
      grid-column: span 4;
    }

    &.is-ai-solutions {
      grid-column: span 4;
    }

    &.is-brand-presence {
      grid-column: span 4;
    }
  }
}

/* Oversized index, quiet enough to read as texture until the card is hovered. */
.services-numeral {
  color: $on-dark-fill-strong;
  font-family: $headings-font-family;
  font-size: 4.75rem;
  font-weight: 700;
  letter-spacing: -0.06em;
  line-height: 1;
  position: absolute;
  right: 1.5rem;
  top: 1.15rem;
  transition: color $dur $ease;
}

/* Hangs off the card's top edge, the same tab the plan table's "Most Popular"
   badge makes. Absolute so it leaves the flow: sitting in the flow pushed the
   icon tile and every line under it down, which left the badged card out of
   step with the other three. */
.services-badge {
  background: $secondary;
  border-radius: $r-xs;
  box-shadow: 0 0 1rem rgba($secondary, .55);
  color: $on-dark-strong;
  font-family: $headings-font-family;
  font-size: 0.72rem;
  font-weight: 600;
  left: var(--card-pad-x);
  letter-spacing: 0.02em;
  line-height: 1;
  padding: 0.4rem 0.75rem;
  position: absolute;
  top: 0;
  transform: translateY(-50%);
  white-space: nowrap;
  z-index: 2;
}

/* White tile carrying the primary icon, the same swap the featured plan
   column makes in the plan table. */
.services-icon {
  align-items: center;
  background: $ground;
  border-radius: $r-md;
  box-shadow: $shadow-lift;
  color: $primary;
  display: inline-flex;
  height: 2.9rem;
  justify-content: center;
  margin-bottom: 1.15rem;
  position: relative;
  width: 2.9rem;
  z-index: 1;

  svg {
    height: 1.1rem;
    width: 1.1rem;
  }
}

/* Card titles are h3: the section head above them is the h2 and the card is a
   level below it. The spec is unchanged from the h4 it used to be. */
.services-card-title {
  color: $on-dark-strong;
  font-size: 1.35rem;
  line-height: 1.25;
  margin-bottom: 0.5rem;
  position: relative;
  z-index: 1;
}

.services-body {
  color: $on-dark-body;
  font-size: 0.95rem;
  line-height: 1.6;
  margin-bottom: 0;
  max-width: 52ch;
  position: relative;
  z-index: 1;
}

/* Only the card's own spacing is decided here. The chip itself - the tint, the
   weight, the padding - belongs to TagList. */
.services-tags {
  margin-top: 1.2rem;
  position: relative;
  z-index: 1;
}

/* Pinned to the bottom so the four links line up across a row however the
   copy above them runs. The chrome (colour, family, size, gap and the arrow)
   is AppButton's `link` variant; only the layout stays here. `align-self`
   is needed because the card is a flex column, so the default `stretch`
   handed the button the whole card width and `justify-content: center`
   then centred the label. */
.services-more {
  align-self: flex-start;
  margin-top: auto;
  padding-top: 1.5rem;
  position: relative;
  z-index: 1;
}

/* Capability ticker. All that is left here is the strip's own chrome: the two
   rules, the padding and where it sits under the bento. The loop itself, the
   edge mask, the pause and the focus ring are Marquee's, because the AI page
   runs the same loop for its model band and there is no second copy of it. */
.services-ticker {
  border-bottom: 1px solid $line-blue;
  border-top: 1px solid $line-blue;
  margin-top: 3rem;
  padding: 1.15rem 0;

  ul {
    display: flex;
    /* Never let a list shrink, or the two halves stop matching and the loop
       seam becomes visible. */
    flex: 0 0 auto;
    margin: 0;
    padding: 0;
  }

  li {
    align-items: center;
    color: $text-muted;
    display: inline-flex;
    font-family: $headings-font-family;
    font-size: 0.8rem;
    font-weight: 600;
    letter-spacing: 0.16em;
    text-transform: uppercase;
    white-space: nowrap;

    &::after {
      background: $line-strong;
      border-radius: 50%;
      content: '';
      height: 4px;
      margin: 0 1.25rem;
      width: 4px;
    }
  }
}
</style>

<script>
import { computed } from 'vue';
import { Link } from '@inertiajs/inertia-vue3';
import { faArrowRight, faBrain, faGlobe, faMagnifyingGlass, faMobileScreen, faPalette } from '@fortawesome/free-solid-svg-icons';
import { library as FontAwesomeLibrary } from '@fortawesome/fontawesome-svg-core';
import { useI18n } from 'vue-i18n';
import AppButton from '@/Components/AppButton.vue';
import Marquee from '@/Components/Marquee.vue';
import SectionTitle from '@/Components/SectionTitle.vue';
import TagList from '@/Components/TagList.vue';
import { hasFinePointer } from '@/pointer';

FontAwesomeLibrary.add(faArrowRight, faBrain, faGlobe, faMagnifyingGlass, faMobileScreen, faPalette);

export default {
  components: {
    AppButton,
    Link,
    Marquee,
    SectionTitle,
    TagList
  },

  data() {
    return {
      onPointerMove: null
    };
  },

  mounted() {
    /* The spotlight is a pointer affordance only. Touch devices have no hover
       to track, so the listener is simply never attached. */
    if (!hasFinePointer()) {
      return;
    }

    this.onPointerMove = (event) => {
      var card = event.target.closest ? event.target.closest('.services-card') : null;
      if (!card) {
        return;
      }

      var rect = card.getBoundingClientRect();
      card.style.setProperty('--mx', ((event.clientX - rect.left) / rect.width) * 100 + '%');
      card.style.setProperty('--my', ((event.clientY - rect.top) / rect.height) * 100 + '%');
    };

    this.$refs.bento.addEventListener('pointermove', this.onPointerMove);
  },

  beforeUnmount() {
    if (this.$refs.bento && this.onPointerMove) {
      this.$refs.bento.removeEventListener('pointermove', this.onPointerMove);
    }
  },

  setup() {
    const { t } = useI18n({
      messages: {
        'en-MY': {
          'Looking for something else?': 'Looking for something else?',
          'Capabilities we cover': 'Capabilities we cover',
          'One-stop professional services': '**One-stop** professional services',
          'A business needs a website buyers trust and a brand that reads as one company. Bought piecemeal, that is three different vendors to chase down.': 'A business needs a website buyers trust and a brand that reads as one company. Bought piecemeal, that is three different vendors to chase down.',
          'Learn more': 'Learn more',
          'Most businesses start here': 'Most businesses start here',
          'A custom website designed, built and hosted by us, with the domain, SSL and email included on one monthly plan.': 'A custom website designed, built and hosted by us, with the domain, SSL and email included on one monthly plan.',
          'SEO and sponsored ads run by the team that builds the site, so the pages people land on are built to convert, not just to rank.': 'SEO and sponsored ads run by the team that builds the site, so the pages people land on are built to convert, not just to rank.',
          'An application your customers keep on the home screen, scoped and built as its own project, with the website behind it.': 'An application your customers keep on the home screen, scoped and built as its own project, with the website behind it.',
          'The logo, the colours and the working files that make your brochure, your name card and your website read as one company.': 'The logo, the colours and the working files that make your brochure, your name card and your website read as one company.',
          Hosting: 'Hosting',
          Domain: 'Domain',
          'Sponsored ads': 'Sponsored ads',
          Analytics: 'Analytics',
          iOS: 'iOS',
          Android: 'Android',
          'Web app': 'Web app',
          'Scoped build': 'Scoped build',
          Logo: 'Logo',
          'Name cards': 'Name cards',
          Brochures: 'Brochures',
          'Print files': 'Print files',
          CDN: 'CDN',
          'Anti-DDoS': 'Anti-DDoS',
        },
        'zh-MY': {
          'Looking for something else?': '还在找其他服务？',
          'Capabilities we cover': '我们涵盖的服务范围',
          'One-stop professional services': '**一站式**专业服务',
          'A business needs a website buyers trust and a brand that reads as one company. Bought piecemeal, that is three different vendors to chase down.': '一家企业要的，是一个买家信得过的网站，还有一个看起来就是同一家公司的品牌。向三四家供应商买，就得整个星期追；在这里，同一个团队全部搞定。',
          'Learn more': '了解更多',
          'Most businesses start here': '多数企业的起点',
          'A custom website designed, built and hosted by us, with the domain, SSL and email included on one monthly plan.': '网站由我们设计、开发并托管，域名、SSL 和邮箱全都包在一个月费里。',
          'SEO and sponsored ads run by the team that builds the site, so the pages people land on are built to convert, not just to rank.': 'SEO 和赞助广告，交给负责建站的那批人来跑。访客点进来看到的页面，是为了成交而做，不只是为了排名。',
          'An application your customers keep on the home screen, scoped and built as its own project, with the website behind it.': '客户会一直留在手机主屏幕上的 App，按项目范围单独来做，网站则是它背后的后盾。',
          'AI that fits the workflow you already run: bots, document reading and automation, built into the tools your team already uses.': '贴合您现有流程的 AI：机器人、文件读取与自动化，直接融入团队正在使用的工具。',
          'The logo, the colours and the working files that make your brochure, your name card and your website read as one company.': '标志、配色，加上可以直接拿去用的源文件，让宣传册、名片和网站看起来就是同一家公司。',
          Hosting: '托管',
          Domain: '域名',
          'Sponsored ads': '赞助广告',
          Analytics: '数据分析',
          iOS: 'iOS',
          Android: 'Android',
          'Web app': '网页应用',
          'Scoped build': '按范围制作',
          Logo: '标志',
          'Name cards': '名片',
          Brochures: '宣传册',
          'Print files': '印刷文件',
          'AI bots': 'AI 机器人',
          Automation: '自动化',
          Reports: '报告',
          'LLM models': 'LLM 模型',
          CDN: 'CDN',
          'Anti-DDoS': '防 DDoS',
        },
        'ms-MY': {
          'Looking for something else?': 'Cari perkhidmatan lain?',
          'Capabilities we cover': 'Keupayaan kami',
          'One-stop professional services': 'Perkhidmatan profesional sehenti',
          'A business needs a website buyers trust and a brand that reads as one company. Bought piecemeal, that is three different vendors to chase down.': 'Perniagaan perlukan laman web yang pembeli percaya dan jenama yang nampak seperti satu syarikat. Dibeli berasingan, anda mengejar tiga vendor.',
          'Learn more': 'Maklumat lanjut',
          'Most businesses start here': 'Kebanyakan bermula di sini',
          'A custom website designed, built and hosted by us, with the domain, SSL and email included on one monthly plan.': 'Laman web tersuai yang kami reka, bina dan hoskan, dengan domain, SSL dan e-mel semuanya termasuk dalam satu bayaran bulanan.',
          'SEO and sponsored ads run by the team that builds the site, so the pages people land on are built to convert, not just to rank.': 'SEO dan iklan tajaan dijalankan oleh pasukan yang sama yang membina laman web, jadi halaman yang pelawat mendarat itu dibina untuk menukar mereka jadi pelanggan, bukan sekadar untuk naik di ranking carian.',
          'An application your customers keep on the home screen, scoped and built as its own project, with the website behind it.': 'Aplikasi yang kekal di skrin utama pelanggan anda, dibina sebagai projek tersendiri mengikut skop yang dipersetujui, dengan laman web sebagai asasnya.',
          'AI that fits the workflow you already run: bots, document reading and automation, built into the tools your team already uses.': 'AI yang menyesuaikan aliran kerja sedia ada anda: bot, pembacaan dokumen dan automasi, dibina ke dalam alatan yang pasukan anda sudah guna.',
          'The logo, the colours and the working files that make your brochure, your name card and your website read as one company.': 'Logo, warna dan fail sumber yang membuatkan risalah, kad nama dan laman web anda kelihatan milik syarikat yang sama.',
          Hosting: 'Hosting',
          Domain: 'Domain',
          'Sponsored ads': 'Iklan tajaan',
          Analytics: 'Analitik',
          iOS: 'iOS',
          Android: 'Android',
          'Web app': 'Aplikasi web',
          'Scoped build': 'Bina ikut skop',
          Logo: 'Logo',
          'Name cards': 'Kad nama',
          Brochures: 'Risalah',
          'Print files': 'Fail cetak',
          'AI bots': 'Bot AI',
          Automation: 'Automasi',
          Reports: 'Laporan',
          'LLM models': 'Model LLM',
          CDN: 'CDN',
          'Anti-DDoS': 'Anti-DDoS',
        }
      }
    });

    /* `name` is the route, so a card can never point somewhere the header
       dropdown does not also offer. */
    const services = computed(() => [
      {
        name: 'web-design',
        icon: 'globe',
        title: t('Web design'),
        badge: t('Most businesses start here'),
        body: t('A custom website designed, built and hosted by us, with the domain, SSL and email included on one monthly plan.'),
        tags: [t('Hosting'), t('Domain'), 'SSL', t('Business email')]
      },
      {
        name: 'digital-marketing',
        icon: 'magnifying-glass',
        title: t('Digital marketing'),
        badge: '',
        body: t('SEO and sponsored ads run by the team that builds the site, so the pages people land on are built to convert, not just to rank.'),
        tags: ['SEO', t('Sponsored ads'), t('Analytics'), t('Monthly report')]
      },
      {
        name: 'mobile-application',
        icon: 'mobile-screen',
        title: t('Mobile application'),
        badge: '',
        body: t('An application your customers keep on the home screen, scoped and built as its own project, with the website behind it.'),
        tags: ['iOS', 'Android', t('Web app'), t('Scoped build')]
      },
      {
        name: 'ai-solutions',
        icon: 'brain',
        title: t('AI Solutions'),
        badge: '',
        body: t('AI that fits the workflow you already run: bots, document reading and automation, built into the tools your team already uses.'),
        tags: [t('AI bots'), t('Automation'), 'CRM', t('Reports')]
      },
      {
        name: 'brand-presence',
        icon: 'palette',
        title: t('Brand presence'),
        badge: '',
        body: t('The logo, the colours and the working files that make your brochure, your name card and your website read as one company.'),
        tags: [t('Logo'), t('Name cards'), t('Brochures'), t('Print files')]
      }
    ]);

    /* Runs under the cards as the receipt for the pitch: the shorter list of
       things a business would otherwise buy from four different vendors. */
    const skills = computed(() => [
      t('Web design'),
      t('Hosting'),
      t('Domain'),
      'SSL',
      t('CDN'),
      t('Anti-DDoS'),
      t('Business email'),
      t('Backups'),
      'SEO',
      t('Sponsored ads'),
      t('Analytics'),
      t('Mobile application'),
      t('AI bots'),
      t('Automation'),
      t('LLM models'),
      t('Logo'),
      t('Print files'),
      t('Brand presence')
    ]);

    return { t, services, skills };
  }
};
</script>
